import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Route, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Container } from 'react-bootstrap'
import * as Sentry from '@sentry/react'
import {
  Paper,
  Grid,
  Box,
  Button,
  Checkbox,
  Menu,
  Select,
  MenuItem,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import {
  canPin,
  customerCareEntries,
  arrangementInForce,
  pinnedEntries,
  toggled,
  movedTo,
} from '../utils/pinnedMenu'
import ParcelsView from './ParcelsView'
import { getMe } from '../redux/actions'
import { sessionEnded } from '../redux/session'
import { getParcels } from '../redux/parcels/slice.parcels'
import { getCarriers } from '../redux/carriers/slice.carriers'
import iBoxenLogo from '../iboxen_logo.png'
import { theme } from '../theme'
import { useMe } from '../hooks/useMe'
import { isIboxenCarrier } from '../utils/permissions'
import LanguageSelector from '../common/LanguageSelector'
import request from '../utils/fetcher'
import ParcelPage from '../common/Parcel/ParcelPage'

// Modals are only rendered on demand, so they live in separate chunks. They
// are preloaded once the browser is idle so opening one stays instant.
const modalLoaders = []
const lazyModal = (load) => {
  let promise
  const loadOnce = () => {
    // Forget a failed load so the next attempt retries instead of failing forever
    promise =
      promise ||
      load().catch((err) => {
        promise = null
        throw err
      })
    return promise
  }
  modalLoaders.push(loadOnce)
  return lazy(loadOnce)
}
const preloadModals = () =>
  // A failed preload is retried when the modal is actually opened
  modalLoaders.forEach((load) => load().catch(() => {}))

const CreateParcelModal = lazyModal(
  () => import('../common/CreateParcel/CreateParcelModal')
)
const MessageSettingsModal = lazyModal(
  () => import('../common/MessageSettingsModal')
)
const ExportModal = lazyModal(() => import('../common/ExportModal'))
const DriversSettingsModal = lazyModal(
  () => import('../common/DriversSettingsModal')
)
const ChangePasswordModal = lazyModal(
  () => import('../common/ChangePasswordModal')
)
const LocationsModal = lazyModal(
  () => import('../common/Locations/LocationsModal')
)
const ControlPanelModal = lazyModal(
  () => import('../common/ControlPanel/ControlPanelModal')
)
const BookingsModal = lazyModal(
  () => import('../common/Bookings/BookingsModal')
)
const LocationControlModal = lazyModal(
  () => import('../common/LocationControl/LocationControlModal')
)
const CarrierLocationIdModal = lazyModal(
  () => import('../common/CarrierLocationId/CarrierLocationIdModal')
)
const ParcelTraceLogsModal = lazyModal(
  () => import('../common/ParcelTraceLogsModal')
)
const UsersModal = lazyModal(() => import('./modals/UsersModal/Modal'))
const LocationModal = lazyModal(() => import('./modals/LocationModal/Modal'))
const RegisterParcelsModal = lazyModal(
  () => import('./modals/RegisterParcelsModal/Modal')
)
const AvalancheModal = lazyModal(() => import('../common/AvalancheModal'))

// one row, so the row stays one row

// The header's columns, so the row below lines up with the buttons above it

export default function Home() {
  const dispatch = useDispatch()

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const closeModal = () => dispatch({ type: 'setShowModal', payload: {} })
  const setShowModal = (modal) =>
    dispatch({ type: 'setShowModal', payload: { modal } })
  const parcelFlow = useSelector((state) => state.parcels.filters.flow)

  const history = useHistory()

  const { t } = useTranslation('carriers')
  const carriers = useSelector((state) => state.carriers)
  const { showModal, selectedParcel, selectedLocation, carrierHeader } =
    useSelector((state) => state.root)
  const { user, carrier } = useMe()

  useEffect(() => {
    if (
      user.isReallySuperUser &&
      !carriers.loading &&
      !carriers.data.carriers.length
    )
      dispatch(getCarriers())
  }, [dispatch, user, carriers])

  useEffect(() => {
    // requestIdleCallback is missing in Safari, fall back to a short delay
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(preloadModals, { timeout: 3000 })
      return () => window.cancelIdleCallback(id)
    }
    const id = setTimeout(preloadModals, 1000)
    return () => clearTimeout(id)
  }, [])

  useEffect(() => {
    dispatch(getMe())
  }, [dispatch, carrierHeader])

  useEffect(() => {
    if (!user || !carrier) return

    if (process.env.NODE_ENV === 'production') {
      window.Intercom('boot', { app_id: 'qb0qj775', name: user.username })
      Sentry.setContext('carrier', {
        _id: carrier._id,
        username: carrier.username,
        name: carrier.name,
      })
      Sentry.setContext('user', {
        _id: user._id,
        username: user.username,
        role: user.role?.niceName,
      })
    }

    if (carrier.shouldSetPassword === true) {
      setShowChangePasswordModal({ requiresOldPassword: false })
    }
  }, [user, carrier, dispatch])

  const [menuAnchor, setMenuAnchor] = useState(null)
  // What the user chose since the page loaded, so the row answers a drag or a tick at once rather
  // than after the next /me. Until they choose, what is stored on them applies — read on every
  // render, because /me resolves after the first one.
  const [chosenMenu, setChosenMenu] = useState(undefined)
  const pinned = arrangementInForce(chosenMenu, user.pinnedMenu)
  const [dragging, setDragging] = useState(null)

  const iboxenMenus = [
    // C2C bookings have no carrier, so only iBoxen staff see them
    {
      modal: 'bookings',
      label: t('title', { ns: 'bookings' }),
      show: carrier.isIboxen,
    },
    // The estate view: every location, station and box iBoxen owns
    {
      modal: 'location-control',
      label: t('title', { ns: 'locationControl' }),
      show: carrier.isIboxen,
    },
    // A carrier's own id for a location: an integration key, kept away from the address edit form.
    // The key stays `dpd-id` although the box is no longer DPD's alone — it is what a pinned row
    // this button sits in was saved as, and renaming it would quietly drop the button out of it.
    {
      modal: 'dpd-id',
      label: t('carrierLocationIdTitle', { ns: 'locationControl' }),
      show: carrier.isIboxen,
    },
    // Consumer login rules and service mode are iBoxen-wide
    {
      modal: 'control-panel',
      label: t('title', { ns: 'controlPanel' }),
      show: carrier.isIboxen && user.isAllowedTo('manageControlPanel'),
    },
  ].filter(({ show }) => show)

  // A carrier that books its own parcels registers them in bulk instead of one at a time
  const usesRegisterParcels = carrier.autoBookEnabled === true

  const logout = () => {
    if (window.Intercom) window.Intercom('shutdown')

    localStorage.removeItem('jwt')
    dispatch(sessionEnded())
    history.push('/login')
  }

  // Every window the header used to offer as a button, in one menu. Three rows of buttons took the
  // top third of the page before anyone had looked at a parcel.
  const allMenuEntries = [
    {
      key: 'create-parcel',
      label: usesRegisterParcels ? t('registerParcels') : t('registerParcel'),
      show: user.isAllowedTo('createParcels'),
      primary: true,
      action: () =>
        setShowModal(
          usesRegisterParcels ? 'register-parcels' : 'create-parcel'
        ),
    },
    {
      key: 'avalanche',
      label: t('avalanche'),
      show: user.isSuperUser && isIboxenCarrier(carrier),
    },
    {
      key: 'manage-users',
      label: t('users'),
      show:
        user.isAllowedTo('manageUsers') ||
        user.isAllowedTo('manageCustomerCareUsers'),
    },
    {
      key: 'message-settings',
      label: t('messages'),
      show: user.isAllowedTo('updateSetting:message'),
    },
    {
      key: 'driver-settings',
      label: t('drivers'),
      show: user.isAllowedTo('editDrivers'),
    },
    {
      key: 'export',
      label: t('export'),
      show: user.isAllowedTo('export'),
      primary: true,
    },
    {
      key: 'locations-settings',
      label: t('locations'),
      show:
        carrier.isAllowedTo('restrictLocations') &&
        user.isAllowedTo('restrictLocations'),
    },
    // the two iBoxen support lives in all day; the other two sit in the overflow
    ...iboxenMenus.map(({ modal, label }) => ({
      key: modal,
      label,
      show: true,
      primary: ['location-control', 'bookings'].includes(modal),
    })),
    {
      key: 'change-password',
      label: t('changePassword'),
      show: true,
      promotable: false,
      action: () => setShowChangePasswordModal(true),
    },
    {
      key: 'logout',
      label: t('logout'),
      show: true,
      promotable: false,
      action: () => logout(),
    },
  ]
    .filter(({ show }) => show)
    .map((entry) => ({
      ...entry,
      action: () => {
        if (entry.action) entry.action()
        else setShowModal(entry.key)
      },
    }))
  // Customer care get bookings, users, password and log out, and the row is not theirs to arrange
  const menuEntries = user.isCustomerCare
    ? customerCareEntries(allMenuEntries)
    : allMenuEntries

  // Which buttons sit in the row is the user's own choice now, kept on them and in the order they
  // put them. Before, it was learnt from what they opened most, which meant the row rearranged
  // itself between sessions and nobody could say why a button had moved.
  //
  // An arrangement they have never made falls back to the ones marked primary, so a new user gets
  // the same row as before. The stored list may name a button they cannot see — a permission can
  // come and go — so the row is the stored order filtered down to what is actually on offer,
  // rather than the stored list itself.
  const primaryEntries = pinnedEntries(
    user.isCustomerCare ? undefined : pinned,
    menuEntries
  )
  const inRowKeys = primaryEntries.map((entry) => entry.key)

  const savePinned = (keys) => {
    setChosenMenu(keys)
    // the row is a convenience: a failed save must not cost the click that made it
    request('PUT', '/me/menu', { pinned: keys }).catch(() => {})
  }

  const togglePinned = (key) => savePinned(toggled(inRowKeys, key))
  const movePinned = (from, to) => savePinned(movedTo(inRowKeys, from, to))
  // the overflow reads alphabetically, since there is no order to learn in it

  return (
    <Container
      fluid
      style={{ paddingLeft: 15, paddingRight: 15, marginTop: 8 }}
    >
      <Suspense fallback={null}>
        {showChangePasswordModal && (
          <ChangePasswordModal
            requiresOldPassword={showChangePasswordModal.requiresOldPassword}
            onClose={() => setShowChangePasswordModal(false)}
          />
        )}
        {showModal === 'create-parcel' && (
          <CreateParcelModal closeModal={closeModal} />
        )}
        {showModal === 'register-parcels' && (
          <RegisterParcelsModal
            closeModal={({ refresh } = {}) => {
              closeModal()
              if (refresh) dispatch(getParcels())
            }}
          />
        )}
        {showModal === 'avalanche' && (
          <AvalancheModal closeModal={closeModal} />
        )}
        {showModal === 'message-settings' && (
          <MessageSettingsModal closeModal={closeModal} />
        )}
        {showModal === 'export' && (
          <ExportModal carrier={carrier} closeModal={closeModal} />
        )}
        {showModal === 'driver-settings' && (
          <DriversSettingsModal closeModal={closeModal} />
        )}
        {showModal === 'locations-settings' && (
          <LocationsModal closeModal={closeModal} />
        )}
        {showModal === 'control-panel' && (
          <ControlPanelModal closeModal={closeModal} />
        )}
        {showModal === 'bookings' && <BookingsModal closeModal={closeModal} />}
        {showModal === 'location-control' && (
          <LocationControlModal closeModal={closeModal} />
        )}
        {showModal === 'dpd-id' && (
          <CarrierLocationIdModal closeModal={closeModal} />
        )}
        {/* The parcel view keeps its own address, so it is a route rather than a modal flag — but
          it renders here, over the list, so closing it leaves the list where it was. */}
        <Route path="/carrier/parcels/:parcelId" component={ParcelPage} />
        {showModal === 'location' ? (
          <>
            <LocationModal
              onClose={closeModal}
              locationId={selectedLocation}
              parcelId={selectedParcel}
            />
          </>
        ) : null}
        {showModal === 'traceLogs' ? (
          <ParcelTraceLogsModal
            closeModal={closeModal}
            parcelId={selectedParcel}
          />
        ) : null}
        {showModal === 'manage-users' ? (
          <UsersModal
            onClose={(event, reason) => {
              if (reason && reason === 'backdropClick') return
              setShowModal(null)
            }}
          />
        ) : null}
        {showModal === 'drivers-settings' ? (
          <DriversSettingsModal closeModal={closeModal} />
        ) : null}
      </Suspense>

      <Paper sx={{ bgcolor: theme(parcelFlow).primary, mb: 1.5 }}>
        <Box sx={{ p: 1 }}>
          <Grid container spacing={1}>
            {/* The carrier sits beside the logo rather than under it: one row, not two */}
            <Grid item sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  alt="iBoxen Logo"
                  src={iBoxenLogo}
                  style={{ width: 128, maxWidth: '100%', flexShrink: 0 }}
                />
                {!carriers.loading &&
                carriers.data.carriers.length > 0 &&
                user.isReallySuperUser ? (
                  <Box>
                    <Select
                      size="small"
                      value={carrierHeader || carrier._id}
                      sx={{ fontSize: '0.75rem', width: 128, maxWidth: '100%' }}
                      onChange={(e) =>
                        dispatch({
                          type: 'carrierHeader',
                          payload: e.target.value,
                        })
                      }
                    >
                      {carriers.data.carriers
                        .slice()
                        .sort((a, b) =>
                          (a.name || '').localeCompare(b.name || '')
                        )
                        .map((c) => (
                          <MenuItem
                            key={c._id}
                            value={c._id}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {c.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                ) : (
                  <h2
                    className="font-weight-bold"
                    style={{ margin: 0, fontSize: '20px' }}
                  >
                    {carrier?.name}
                  </h2>
                )}
              </Box>
            </Grid>
            <Grid item sm={9}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {/* Dragged to reorder. The drop lands where the button you dropped on sits,
                    so dragging one left puts it there and pushes the rest along. */}
                {primaryEntries.map(({ key, label, action }, index) => (
                  <Button
                    key={key}
                    color="iboxen"
                    onClick={action}
                    draggable={!user.isCustomerCare}
                    onDragStart={() => setDragging(index)}
                    onDragEnd={() => setDragging(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (dragging !== null) movePinned(dragging, index)
                      setDragging(null)
                    }}
                    sx={{
                      bgcolor: 'white',
                      ':hover': { bgcolor: 'grey.200' },
                      cursor: dragging === null ? 'pointer' : 'grabbing',
                      opacity: dragging === index ? 0.5 : 1,
                    }}
                  >
                    {label}
                  </Button>
                ))}
                {/* the overflow reads as one more button, not as a hamburger beside them */}
                <Button
                  color="iboxen"
                  endIcon={<ArrowDropDown />}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ bgcolor: 'white', ':hover': { bgcolor: 'grey.200' } }}
                >
                  {t('more', { ns: 'common' })}
                </Button>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {/* The tick is what puts a button in the row, so every window that can be
                      pinned is listed here — including the ones already in it, or there would be
                      nowhere to untick them. The label still opens the window. */}
                  {menuEntries.map((entry) => {
                    const inRow = inRowKeys.includes(entry.key)
                    if (inRow && (!canPin(entry) || user.isCustomerCare))
                      return null
                    return (
                      <MenuItem
                        key={entry.key}
                        onClick={() => {
                          setMenuAnchor(null)
                          entry.action()
                        }}
                        sx={{ pl: 0.5 }}
                      >
                        <Checkbox
                          size="small"
                          checked={inRow}
                          disabled={!canPin(entry)}
                          inputProps={{
                            'aria-label': t('pinToRow', {
                              ns: 'common',
                              label: entry.label,
                            }),
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            togglePinned(entry.key)
                          }}
                        />
                        {entry.label}
                      </MenuItem>
                    )
                  })}
                </Menu>
                <Box
                  alignItems="center"
                  justifyContent="flex-end"
                  display="flex"
                  marginTop="3px"
                >
                  <LanguageSelector />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <ParcelsView />
    </Container>
  )
}
